import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Users as UsersIcon, KeyRound, Pencil } from "lucide-react";
import { TID } from "@/constants/testIds";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/Avatar";

export default function Team() {
  const { user, refresh } = useAuth();
  const isSuper = !!user?.is_super_admin;
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamRoles, setTeamRoles] = useState({});
  const [filterTeam, setFilterTeam] = useState("ALL");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "editor", team: "", title: "Editor", password: "" });
  const [saving, setSaving] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // member being edited
  const [editForm, setEditForm] = useState({ name: "", role: "editor", team: "", title: "Editor" });
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    const [uRes, tRes] = await Promise.all([
      api.get("/users"),
      api.get("/teams").catch(() => ({ data: { teams: [], roles: {} } })),
    ]);
    setUsers(uRes.data);
    setTeams(tRes.data.teams || []);
    setTeamRoles(tRes.data.roles || {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (user?.team) setForm((f) => ({ ...f, team: user.team, title: (teamRoles[user.team] || ["Editor"])[teamRoles[user.team]?.length ? 1 : 0] || "Editor" }));
  }, [user, teamRoles]);

  // When the team changes in the Add-Member form, reset title to a valid one for that team
  useEffect(() => {
    const allowed = teamRoles[form.team] || [];
    if (form.title && allowed.includes(form.title)) return;
    if (allowed.length) setForm((f) => ({ ...f, title: allowed[allowed.length > 1 ? 1 : 0] }));
  }, [form.team, form.title, teamRoles]);

  // Same for the Edit dialog
  useEffect(() => {
    const allowed = teamRoles[editForm.team] || [];
    if (!allowed.length || (editForm.title && allowed.includes(editForm.title))) return;
    setEditForm((f) => ({ ...f, title: allowed[allowed.length > 1 ? 1 : 0] }));
  }, [editForm.team, editForm.title, teamRoles]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.password || form.password.length < 6) {
      toast.error("Password (min 6 chars) is required — you'll share it with the member.");
      return;
    }
    setSaving(true);
    try {
      const team = isSuper ? form.team : user.team;
      await api.post("/users", {
        name: form.name,
        email: form.email.trim().toLowerCase(),
        title: form.title,
        team,
        password: form.password,
      });
      toast.success("Member added — share the login credentials");
      setForm({ name: "", email: "", role: "editor", team, title: (teamRoles[team] || ["Editor"])[teamRoles[team]?.length > 1 ? 1 : 0], password: "" });
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name} from the team? This deletes their tasks too.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Member removed");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (resetPw.length < 6) { toast.error("Password must be 6+ chars"); return; }
    setResetSaving(true);
    try {
      await api.post(`/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetTarget.name}`);
      setResetTarget(null); setResetPw("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally {
      setResetSaving(false);
    }
  };

  const openEdit = (member) => {
    setEditTarget(member);
    setEditForm({
      name: member.name,
      role: member.role,
      team: member.team,
      title: member.title || (member.role === "manager" ? "Manager" : "Editor"),
      is_super_admin: !!member.is_super_admin,
      observer_teams: Array.isArray(member.observer_teams) ? [...member.observer_teams] : [],
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error("Name required"); return; }
    setEditSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        title: editForm.title,
      };
      if (isSuper) {
        payload.team = editForm.team;
        payload.is_super_admin = !!editForm.is_super_admin;
        // Observer teams: only meaningful for managers. Strip the primary team
        // if the user accidentally picked it — backend will do the same.
        payload.observer_teams = (editForm.observer_teams || []).filter((t) => t && t !== editForm.team);
      }
      await api.patch(`/users/${editTarget.id}`, payload);
      toast.success(`Updated ${editForm.name}`);
      // If we just edited ourselves, refresh AuthContext so the sidebar/RBAC reflect it instantly
      if (editTarget.id === user?.id) await refresh?.();
      setEditTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setEditSaving(false);
    }
  };

  const [search, setSearch] = useState("");
  const visibleUsers = useMemo(() => {
    let out = users;
    if (isSuper && filterTeam !== "ALL") out = out.filter((u) => u.team === filterTeam);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.title || "").toLowerCase().includes(q));
    }
    return out;
  }, [users, filterTeam, isSuper, search]);

  const groupedByTeam = useMemo(() => {
    if (!isSuper) return null;
    const map = new Map();
    (teams.length ? teams : Array.from(new Set(users.map((u) => u.team))))
      .forEach((t) => map.set(t, []));
    const q = search.trim().toLowerCase();
    users.forEach((u) => {
      if (q && !((u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q) || (u.title || "").toLowerCase().includes(q))) return;
      if (!map.has(u.team)) map.set(u.team, []);
      map.get(u.team).push(u);
    });
    return map;
  }, [users, teams, isSuper, search]);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between items-end mb-6 flex-wrap gap-3">
        <div>
          <div className="overline flex items-center gap-2"><UsersIcon size={12} /> Team</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mt-1">
            {isSuper ? "All Teams" : user?.team}
          </h1>
          <p className="text-sm text-white/50 mt-1">
            {isSuper
              ? "As a super admin you can manage every team's roster and pick any team when adding a new member."
              : "Manage members of your team. Any email works — set a password of your choice and share it."}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Input
            data-testid="team-search-input"
            placeholder="Search by name, email, title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-black/40 border-white/10 w-56 focus-visible:ring-red-500"
          />
          {isSuper && (
            <Select value={filterTeam} onValueChange={setFilterTeam}>
              <SelectTrigger data-testid="team-filter-select" className="bg-black/40 border-white/10 w-52">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent className="bg-surface border-white/10 text-white">
                <SelectItem value="ALL">All teams</SelectItem>
                {teams.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid={TID.memberAddBtn} className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full">
                <Plus size={16} className="mr-1" /> Add Member
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Add Team Member</DialogTitle>
                <DialogDescription className="text-white/50">
                  {isSuper
                    ? "As super admin, you can add to any team."
                    : <>Adds to <span className="text-white/80">{user?.team}</span>. Any email works.</>}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label className="overline">Name</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="member-name-input"
                    className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500" />
                </div>
                <div>
                  <Label className="overline">Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder=""
                    data-testid="member-email-input"
                    className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500" />
                </div>
                <div>
                  <Label className="overline">Title</Label>
                  <Select value={form.title} onValueChange={(v) => setForm({ ...form, title: v })}>
                    <SelectTrigger data-testid="member-role-select" className="bg-black/40 border-white/10 mt-2">
                      <SelectValue placeholder="Select a title" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-white/10 text-white">
                      {(teamRoles[isSuper ? form.team : user?.team] || ["Manager", "Editor"]).map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-white/40 mt-1">
                    Titles like <span className="text-white/70">Manager</span>, <span className="text-white/70">Social Media Manager</span>, <span className="text-white/70">Founder</span> or <span className="text-white/70">Director</span> grant admin access to their team.
                  </p>
                </div>
                <div>
                  <Label className="overline">Team</Label>
                  {isSuper ? (
                    <Select value={form.team} onValueChange={(v) => setForm({ ...form, team: v })}>
                      <SelectTrigger data-testid="member-team-select" className="bg-black/40 border-white/10 mt-2">
                        <SelectValue placeholder="Choose team" />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-white/10 text-white">
                        {teams.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-2 px-3 py-2 rounded-md bg-black/40 border border-white/10 text-sm text-white/70">
                      {user?.team}
                      <span className="text-[0.7rem] text-white/40 ml-2">(managers can only add to their own team)</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label className="overline">Password</Label>
                  <Input type="text" required minLength={6}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="member-password-input"
                    placeholder="Give them this password"
                    className="bg-black/40 border-white/10 mt-2 font-mono focus-visible:ring-red-500" />
                  <p className="text-xs text-white/40 mt-1">Share this with the member — they&apos;ll use it to sign in.</p>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="member-submit-btn"
                    className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-6">
                    {saving ? "Adding…" : "Add member"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isSuper && filterTeam === "ALL" ? (
        <div className="space-y-8">
          {Array.from(groupedByTeam?.entries() || []).map(([teamName, memberList]) => (
            <div key={teamName}>
              <div className="flex items-center gap-2 mb-3">
                <div className="text-[0.7rem] uppercase tracking-widest text-[#E11D2E] bg-[#E11D2E]/10 border border-[#E11D2E]/30 rounded px-2 py-0.5">{teamName}</div>
                <div className="text-xs text-white/40 font-mono">{memberList.length} member{memberList.length === 1 ? "" : "s"}</div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {memberList.map((u) => (
                  <MemberCard key={u.id} member={u} currentUser={user} isSuper={isSuper} onEdit={() => openEdit(u)} onReset={() => { setResetTarget(u); setResetPw(""); }} onRemove={() => remove(u)} />
                ))}
                {memberList.length === 0 && (
                  <div className="text-xs text-white/30 italic p-4 border border-dashed border-white/8 rounded-md">
                    No members yet.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleUsers.map((u) => (
            <MemberCard key={u.id} member={u} currentUser={user} isSuper={isSuper} onEdit={() => openEdit(u)} onReset={() => { setResetTarget(u); setResetPw(""); }} onRemove={() => remove(u)} />
          ))}
        </div>
      )}

      <Dialog open={!!editTarget} onOpenChange={(o) => { if (!o) setEditTarget(null); }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Edit profile — {editTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Change the name, role, or team for this member.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <div>
              <Label className="overline">Name</Label>
              <Input required value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                data-testid="edit-member-name"
                className="bg-black/40 border-white/10 mt-2 focus-visible:ring-red-500" />
            </div>
            <div>
              <Label className="overline">Email</Label>
              <div className="mt-2 px-3 py-2 rounded-md bg-black/40 border border-white/10 text-sm text-white/60 truncate">
                {editTarget?.email}
                <span className="text-[0.7rem] text-white/30 ml-2">(email is fixed — reset password to give access)</span>
              </div>
            </div>
            <div>
              <Label className="overline">Title</Label>
              <Select value={editForm.title} onValueChange={(v) => setEditForm({ ...editForm, title: v })}>
                <SelectTrigger data-testid="edit-member-role" className="bg-black/40 border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">
                  {(teamRoles[editForm.team] || ["Manager", "Editor"]).map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Team / Department</Label>
              {isSuper ? (
                <Select value={editForm.team} onValueChange={(v) => setEditForm({ ...editForm, team: v })}>
                  <SelectTrigger data-testid="edit-member-team" className="bg-black/40 border-white/10 mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-surface border-white/10 text-white">
                    {teams.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="mt-2 px-3 py-2 rounded-md bg-black/40 border border-white/10 text-sm text-white/60">
                  {editTarget?.team}
                  <span className="text-[0.7rem] text-white/30 ml-2">(only super admins can move a member to another team)</span>
                </div>
              )}
            </div>
            {isSuper && (
              <div
                data-testid="edit-member-super-admin-row"
                className="flex items-center justify-between border border-amber-500/20 bg-amber-500/5 rounded-md p-3"
              >
                <div className="pr-3">
                  <div className="text-sm font-medium text-amber-100 flex items-center gap-2">
                    Super Admin
                    {editTarget?.id === user?.id && (
                      <span className="text-[0.65rem] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">You</span>
                    )}
                  </div>
                  <div className="text-xs text-amber-100/70 mt-0.5 leading-snug">
                    Full cross-team access. Can create/delete anyone, manage every team, and promote others.
                  </div>
                </div>
                <Switch
                  data-testid="edit-member-super-admin-switch"
                  checked={!!editForm.is_super_admin}
                  onCheckedChange={(v) => setEditForm({ ...editForm, is_super_admin: v })}
                  disabled={editTarget?.id === user?.id}
                />
              </div>
            )}
            {isSuper && editTarget?.role === "manager" && (
              <div data-testid="edit-member-observer-teams-row" className="border border-sky-500/20 bg-sky-500/5 rounded-md p-3">
                <div className="text-sm font-medium text-sky-100 mb-1">Observer teams</div>
                <div className="text-xs text-sky-100/70 mb-2 leading-snug">
                  Extra teams this manager can watch (read-only cross-team access).
                  Their primary team (<span className="text-sky-200">{editForm.team}</span>) is always included.
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {teams.filter((t) => t !== editForm.team).map((t) => {
                    const checked = (editForm.observer_teams || []).includes(t);
                    return (
                      <button
                        key={t}
                        type="button"
                        data-testid={`edit-member-observer-team-${t.replace(/\W+/g, "-")}`}
                        onClick={() => {
                          setEditForm((f) => ({
                            ...f,
                            observer_teams: checked
                              ? (f.observer_teams || []).filter((x) => x !== t)
                              : [...(f.observer_teams || []), t],
                          }));
                        }}
                        className={`text-xs px-2.5 py-1 rounded-full border transition ${
                          checked
                            ? "bg-sky-500/25 text-sky-100 border-sky-500/60"
                            : "text-white/60 border-white/15 hover:border-sky-500/40 hover:text-sky-200"
                        }`}
                      >{t}</button>
                    );
                  })}
                  {teams.filter((t) => t !== editForm.team).length === 0 && (
                    <span className="text-xs text-white/40 italic">No other teams available.</span>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={editSaving}
                data-testid="edit-member-submit"
                className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-6">
                {editSaving ? "Saving…" : "Save changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              Reset password — {resetTarget?.name}
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Set a new password and share it with the member.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={doReset} className="space-y-4">
            <div>
              <Label className="overline">New password</Label>
              <Input
                type="text" required minLength={6}
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                data-testid="reset-password-input"
                placeholder="Give them this password"
                className="bg-black/40 border-white/10 mt-2 font-mono focus-visible:ring-red-500"
              />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={resetSaving}
                data-testid="reset-password-submit"
                className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-6">
                {resetSaving ? "Resetting…" : "Reset password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MemberCard({ member, currentUser, isSuper, onEdit, onReset, onRemove }) {
  return (
    <div className="rounded-xl border border-white/8 bg-surface p-4 flex items-center gap-3">
      <Avatar user={member} size={42} />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate flex items-center gap-2">
          {member.name}
          {member.is_super_admin && (
            <span className="text-[0.65rem] uppercase tracking-widest text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">Super</span>
          )}
          {member.role === "manager" && !member.is_super_admin && (
            <span className="text-[0.65rem] uppercase tracking-widest text-[#E11D2E] bg-[#E11D2E]/10 border border-[#E11D2E]/30 rounded px-1.5 py-0.5">{member.title || "Manager"}</span>
          )}
          {member.role !== "manager" && member.title && member.title !== "Editor" && (
            <span className="text-[0.65rem] uppercase tracking-widest text-white/70 bg-white/5 border border-white/10 rounded px-1.5 py-0.5">{member.title}</span>
          )}
        </div>
        <div className="text-xs text-white/40 truncate">{member.email}</div>
        <div className="text-[0.7rem] text-white/30 mt-0.5 font-mono">{member.team}{member.title && member.title !== "Manager" && member.title !== "Editor" ? " · " + member.title : ""}</div>
      </div>
      <div className="flex flex-col gap-1">
        {isSuper && (
          <Button variant="ghost" size="sm"
            data-testid={`member-edit-btn-${member.id}`}
            onClick={onEdit}
            className="text-white/40 hover:text-white hover:bg-white/5 h-7 px-2"
            title="Edit profile">
            <Pencil size={13} />
          </Button>
        )}
        <Button variant="ghost" size="sm"
          data-testid={`member-reset-btn-${member.id}`}
          onClick={onReset}
          className="text-white/40 hover:text-white hover:bg-white/5 h-7 px-2"
          title="Reset password">
          <KeyRound size={13} />
        </Button>
        {member.id !== currentUser?.id && (
          <Button variant="ghost" size="sm"
            data-testid={TID.memberDeleteBtn(member.id)}
            onClick={onRemove}
            className="text-white/40 hover:text-[#E11D2E] hover:bg-[#E11D2E]/10 h-7 px-2"
            title="Remove member">
            <Trash2 size={13} />
          </Button>
        )}
      </div>
    </div>
  );
}
